import {DownloadIcon, LinkExternalIcon, TerminalIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, ButtonGroup, FormControl, IconButton, Textarea} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'

import {downloadDebugLog, formatDebugLog, type AhpDebugEvent} from '../utils/ahp-debug-log'
import styles from './DebugLogButton.module.css'

export interface DebugLogButtonProps {
  debugEvents: readonly AhpDebugEvent[]
  defaultOpen?: boolean
  size?: 'small' | 'medium'
  testId?: string
  activationTraceUrl?: string
  connectionTraceUrl?: string
}

/**
 * Staff-facing AHP transport log viewer. Rendered by Quick Chat and Mission
 * Control so both surfaces expose the same downloadable relay log.
 */
export function DebugLogButton({
  debugEvents,
  defaultOpen = false,
  size = 'small',
  testId,
  activationTraceUrl,
  connectionTraceUrl,
}: DebugLogButtonProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const debugLog = useMemo(() => (isOpen ? formatDebugLog(debugEvents) : ''), [isOpen, debugEvents])

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const download = useCallback(() => downloadDebugLog(formatDebugLog(debugEvents)), [debugEvents])

  const count = debugEvents.length
  const label = count > 0 ? `View debug log (${count} ${count === 1 ? 'event' : 'events'})` : 'View debug log'

  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={open}
      onClose={close}
      width="xxlarge"
      height="auto"
      focusZoneSettings={{disabled: true}}
      overlayProps={{role: 'dialog', 'aria-label': 'Debug log', 'aria-modal': true}}
      renderAnchor={anchorProps => (
        <IconButton
          {...anchorProps}
          aria-haspopup="dialog"
          icon={TerminalIcon}
          aria-label={label}
          size={size}
          {...(testId ? {'data-testid': testId} : {})}
        />
      )}
    >
      <div className={styles.DebugPopover}>
        {count > 0 ? (
          <>
            <FormControl>
              <FormControl.Label>Debug output</FormControl.Label>
              <Textarea block className={styles.DebugTextarea} readOnly rows={12} value={debugLog} />
            </FormControl>
          </>
        ) : (
          <p className={styles.DebugEmptyDescription}>No debug events captured yet.</p>
        )}
        <div className={styles.DebugFooter}>
          <div className={styles.DebugActions}>
            {count > 0 && (
              <Button leadingVisual={DownloadIcon} onClick={download} type="button">
                Download logs
              </Button>
            )}
            {activationTraceUrl && (
              <Button
                as="a"
                href={activationTraceUrl}
                leadingVisual={LinkExternalIcon}
                rel="noopener noreferrer"
                target="_blank"
              >
                View activation trace in Datadog
              </Button>
            )}
            {connectionTraceUrl && (
              <Button
                as="a"
                href={connectionTraceUrl}
                leadingVisual={LinkExternalIcon}
                rel="noopener noreferrer"
                target="_blank"
              >
                View connection trace in Datadog
              </Button>
            )}
          </div>
          <ButtonGroup>
            <Button onClick={close} type="button">
              Close
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </AnchoredOverlay>
  )
}
